import type {ComponentProps, ReactNode} from 'react';
import {toast as sonnerToast, Toaster as Sonner} from 'sonner';

import useTheme from '@/hooks/useTheme';
import {twx} from '@/utils/tailwindUtils';

import Toast from './Toast';
import styles from './Toaster.module.scss';

export type ToastAction = {
    label: string;
    onClick?: () => void;
};

export type ShowToastOptions = {
    action?: ToastAction;
    description?: ReactNode;
    duration?: number;
} & Record<string, unknown>;

type ToasterProps = ComponentProps<typeof Sonner>;
const DEFAULT_TOAST_DURATION = 4000;

const Toaster = (props: ToasterProps) => {
    const {isThemeDark} = useTheme();

    return (
        <Sonner
            // @ts-ignore
            className={styles.Toaster}
            toastOptions={{
                style: {
                    // @ts-ignore
                    '--width': '360px',
                },
                classNames: {
                    title: 'w-full',
                    content: 'w-full',
                    toast: twx(
                        'min-[600px]:w-full border-none text-14 tracking-04 font-normal shadow-4px py-3 px-6 flex rounded-lg',
                        isThemeDark ? 'bg-secondary' : 'bg-primary text-white'
                    ),
                },
                duration: DEFAULT_TOAST_DURATION,
            }}
            offset={{bottom: '64px'}}
            mobileOffset={
                isThemeDark
                    ? {bottom: '34px', left: '24px', right: '24px'}
                    : {bottom: '34px', left: '16px', right: '16px'}
            }
            position='bottom-center'
            {...props}
        />
    );
};

export default Toaster;

export const showToast = (
    title: ReactNode,
    options: ShowToastOptions = {}
): ReturnType<typeof sonnerToast.custom> | void => {
    const {action, description, duration = DEFAULT_TOAST_DURATION, ...rest} = options;
    const hasContent = Boolean(title || description);

    if (!hasContent) {
        return;
    }

    const normalizedAction = action?.label ? action : undefined;

    return sonnerToast.custom(
        (id) => <Toast id={id} title={title} description={description} action={normalizedAction} />,
        {
            duration,
            ...rest,
        }
    );
};
